import React, { useMemo } from "react";
import dayjs from "dayjs";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
    DefaultLegendContent,
} from "recharts";

export type ProfitStructureBreakdownYoYRow = {
    month: string; // "YYYY/MM"

    // 前期（前年同月）
    sales_prev: number;
    cost_prev: number;
    fee_prev: number;
    logistics_prev: number;
    promotion_prev: number;
    marginalProfit_prev: number;
    fixedCost_prev: number;
    operatingProfit_prev: number;

    // 今期
    sales_cur: number;
    cost_cur: number;
    fee_cur: number;
    logistics_cur: number;
    promotion_cur: number;
    marginalProfit_cur: number;
    fixedCost_cur: number;
    operatingProfit_cur: number;
};

type Props = {
    data: ProfitStructureBreakdownYoYRow[];
    loading?: boolean;
    emptyLabel?: string;
    title?: string;

    // ✅ OFF: 今期のみ / ON: 前期+今期
    showCompare?: boolean;
    onChangeShowCompare?: (v: boolean) => void;
    compareLabel?: string;
};

type ChartRow = {
    x: string; // "YYYY/MM|前期" etc

    sales: number;
    cost: number;
    fee: number;
    logistics: number;
    promotion: number;
    marginalProfit: number;
    fixedCost: number;
    operatingProfit: number;
};

const LEGEND_ORDER = [
    "sales",
    "cost",
    "fee",
    "logistics",
    "promotion",
    "marginalProfit",
    "fixedCost",
    "operatingProfit",
];

function fmtCompactYen(v: number) {
    const n = Number(v || 0);
    const a = Math.abs(n);
    if (a >= 1e8) return `${(n / 1e8).toFixed(1)}億`;
    if (a >= 1e4) return `${(n / 1e4).toFixed(1)}万`;
    return `${Math.round(n).toLocaleString("ja-JP")}`;
}

function fmtYen(v: number) {
    return `${Math.round(Number(v || 0)).toLocaleString("ja-JP")}円`;
}

function sumRow(r: any) {
    return (
        (Number(r?.sales ?? 0) || 0) +
        (Number(r?.cost ?? 0) || 0) +
        (Number(r?.fee ?? 0) || 0) +
        (Number(r?.logistics ?? 0) || 0) +
        (Number(r?.promotion ?? 0) || 0) +
        (Number(r?.marginalProfit ?? 0) || 0) +
        (Number(r?.fixedCost ?? 0) || 0) +
        (Number(r?.operatingProfit ?? 0) || 0)
    );
}

export default function ProfitStructureBreakdownYoYChart({
    data,
    loading = false,
    emptyLabel = "データがありません",
    title = "利益構造（売上・原価額・手数料・物流費・販促費・限界利益額・固定費・営業利益）",
    showCompare = false,
    onChangeShowCompare,
    compareLabel = "前期比較（前年同月）",
}: Props) {
    const hasData = Array.isArray(data) && data.length > 0;

    const BASE = {
        blue: "#5C97E3",
        yellow: "#FED561",
        orange: "#FEB072",
        red: "#FB7772",
        cyan: "#5DBEE9",
    };

    const MIX = {
        blueCyan: "#5CAAE6", // blue + cyan
        yellowOrange: "#FEC26A", // yellow + orange
        orangeRed: "#FC9472", // orange + red
    };
    const ADD = {
        deepBlue: "#2F6FE6", // ベース青を締めるロイヤルブルー
        deepCyan: "#00A6D6", // シアンを締めるオーシャン系
        indigo: "#6F5CF0", // 青系の延長にあるインディゴ
    };

    const DARK = {
        blue: "#4571AA",
        red: "#BC5956",
        cyan: "#468EAF",
        // 必要ならさらに：
        orange: "#BE8456",
        yellow: "#BEA049",
    };

    const COLORS_PROFIT = {
        sales: BASE.cyan,
        cost: BASE.red,
        fee: BASE.orange,
        logistics: BASE.yellow,
        promotion: BASE.blue,
        marginalProfit: ADD.deepBlue,
        fixedCost: ADD.deepCyan,
        operatingProfit: ADD.indigo,
    };


    // ✅ 販促費内訳と同じ：month+prev/cur を「前期行」「今期行」に展開
    const chartData: ChartRow[] = useMemo(() => {
        const out: ChartRow[] = [];
        for (const r of data || []) {
            const m = r.month;

            // "YYYY/MM" → "YYYY-MM-01"
            const prevM = (() => {
                const ym = String(m).replace(/\//g, "-"); // "YYYY-MM"
                const d = dayjs(ym + "-01");
                return d.isValid() ? d.subtract(1, "year").format("YYYY/MM") : String(m);
            })();

            if (showCompare) {
                out.push({
                    x: `${prevM}|前期`,
                    sales: Number(r.sales_prev ?? 0) || 0,
                    cost: Number(r.cost_prev ?? 0) || 0,
                    fee: Number(r.fee_prev ?? 0) || 0,
                    logistics: Number(r.logistics_prev ?? 0) || 0,
                    promotion: Number(r.promotion_prev ?? 0) || 0,
                    marginalProfit: Number(r.marginalProfit_prev ?? 0) || 0,
                    fixedCost: Number(r.fixedCost_prev ?? 0) || 0,
                    operatingProfit: Number(r.operatingProfit_prev ?? 0) || 0,
                });
            }

            out.push({
                x: `${m}|今期`,
                sales: Number(r.sales_cur ?? 0) || 0,
                cost: Number(r.cost_cur ?? 0) || 0,
                fee: Number(r.fee_cur ?? 0) || 0,
                logistics: Number(r.logistics_cur ?? 0) || 0,
                promotion: Number(r.promotion_cur ?? 0) || 0,
                marginalProfit: Number(r.marginalProfit_cur ?? 0) || 0,
                fixedCost: Number(r.fixedCost_cur ?? 0) || 0,
                operatingProfit: Number(r.operatingProfit_cur ?? 0) || 0,
            });
        }
        return out;
    }, [data, showCompare]);

    // ✅ X軸：各棒の下に「年月」だけ出す（販促費内訳と同じ）
    const MultiLineTick = (props: any) => {
        const { x, y, payload } = props;
        const raw = String(payload?.value ?? "");
        const [m, p] = raw.split("|");
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} textAnchor="end" fill="#666" fontSize={11} transform="rotate(-45)">
                    {m}
                </text>
            </g>
        );
    };

    return (
        <div className="rounded-2xl border bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-xs font-semibold text-slate-800">{title}</div>

                {onChangeShowCompare && (
                    <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
                        <input
                            type="checkbox"
                            checked={showCompare}
                            onChange={(e) => onChangeShowCompare(e.target.checked)}
                        />
                        {compareLabel}
                    </label>
                )}
            </div>

            {!hasData ? (
                <div className="text-sm text-slate-500">
                    {loading ? "読み込み中..." : emptyLabel}
                </div>
            ) : (
                <div style={{ width: "100%", height: 320 }}>
                    <ResponsiveContainer>
                        <BarChart
                            key={showCompare ? "cmp-on" : "cmp-off"}
                            data={chartData}
                            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                            barCategoryGap={14}
                            barGap={4}
                        >
                            <CartesianGrid strokeDasharray="3 3" />

                            <XAxis
                                dataKey="x"
                                tickMargin={12}
                                tick={<MultiLineTick />}
                                height={64}
                                interval={0}
                                minTickGap={16}
                                padding={{ left: 30, right: 40 }}
                            />

                            <YAxis
                                tickFormatter={(v) => fmtCompactYen(Number(v))}
                                width={56}
                                tick={{ fontSize: 11 }}
                            />

                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (!active || !payload || !payload.length) return null;

                                    const row: any = payload?.[0]?.payload ?? {};
                                    const raw = String(label ?? "");
                                    const [m, p] = raw.split("|");

                                    const total = sumRow(row);

                                    return (
                                        <div className="rounded-lg border bg-white px-3 py-2 text-xs shadow">
                                            <div className="mb-1 font-semibold">
                                                {m}（{p}）
                                            </div>

                                            <div className="space-y-1 text-slate-700">
                                                <div className="font-semibold">合計: {fmtYen(total)}</div>
                                                <div>売上: {fmtYen(row.sales)}</div>
                                                <div>原価額: {fmtYen(row.cost)}</div>
                                                <div>手数料: {fmtYen(row.fee)}</div>
                                                <div>物流費: {fmtYen(row.logistics)}</div>
                                                <div>販促費: {fmtYen(row.promotion)}</div>
                                                <div>限界利益額: {fmtYen(row.marginalProfit)}</div>
                                                <div>固定費: {fmtYen(row.fixedCost)}</div>
                                                <div>営業利益: {fmtYen(row.operatingProfit)}</div>
                                            </div>
                                        </div>
                                    );
                                }}
                            />

                            {/* ✅ 販促費内訳と同じ：全て stackId="a" の積み上げ */}
                            <Bar dataKey="sales" stackId="a" name="売上" fill={COLORS_PROFIT.sales} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="cost" stackId="a" name="原価額" fill={COLORS_PROFIT.cost} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="fee" stackId="a" name="手数料" fill={COLORS_PROFIT.fee} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="logistics" stackId="a" name="物流費" fill={COLORS_PROFIT.logistics} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="promotion" stackId="a" name="販促費" fill={COLORS_PROFIT.promotion} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="marginalProfit" stackId="a" name="限界利益額" fill={COLORS_PROFIT.marginalProfit} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="fixedCost" stackId="a" name="固定費" fill={COLORS_PROFIT.fixedCost} barSize={28} isAnimationActive={false} />
                            <Bar dataKey="operatingProfit" stackId="a" name="営業利益" fill={COLORS_PROFIT.operatingProfit} barSize={28} isAnimationActive={false} />

                            <Legend
                                wrapperStyle={{ fontSize: 11 }}
                                content={(props: any) => {
                                    const payload = props?.payload || [];
                                    const sorted = [...payload].sort((a: any, b: any) => {
                                        const ai = LEGEND_ORDER.indexOf(a.dataKey);
                                        const bi = LEGEND_ORDER.indexOf(b.dataKey);
                                        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                                    });
                                    return <DefaultLegendContent {...props} payload={sorted} />;
                                }}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}
